/*eslint-disable*/
import React from "react";
import PropTypes from "prop-types";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";

// Material UI Components
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardIcon from "components/Card/CardIcon.js";
import CardBody from "components/Card/CardBody.js";

// Icons
import Assignment from "@material-ui/icons/Assignment";

// Local imports - modular files
import { USER_TYPES, STAT_TYPES } from "../../constants/adminConstants";
import { useAdminState } from "../../hooks/useAdminState";
import {
  addUser,
  updateUser,
  deleteUser,
  deleteTentative,
  approveTentative,
} from "../../services/adminApi";
import {
  validateEmail,
  getUserTypeCode,
  filterUsersByType,
  getEmailList,
  processVoteData,
  processRegistrationData,
  processUserData,
  processAttendanceData,
  processDataRequestsData,
  processTentativeData,
} from "../../utils/adminUtils";
import AlertManager from "../../components/Admin/AlertManager";
import UserModal from "../../components/Admin/UserModal";
import StatsRenderer from "../../components/Admin/StatsRenderer";
import TentativeTable from "../../components/Admin/TentativeTable";
import RegistrationInfoModal from "../../components/Admin/RegistrationInfoModal";
import DataRequestsInfoModal from "../../components/Admin/DataRequestsInfoModal";

// Styles
import { cardTitle } from "assets/jss/material-dashboard-pro-react.js";

const stylesCard = {
  cardIconTitle: {
    ...cardTitle,
    marginTop: "15px",
    marginBottom: "0px",
    textAlign: "left",
  },
};

const useStylesCard = makeStyles(stylesCard);

export default function Admin(props) {
  const classes = useStylesCard();

  // Custom hooks
  const {
    formData,
    formStates,
    modals,
    alert,
    setAlert,
    updateFormData,
    updateFormState,
    openModal,
    closeModal,
    resetForm,
  } = useAdminState();

  // User Management Functions
  const handleSubmitAddUser = async (e) => {
    e.preventDefault();

    const postValues = {
      email: formData.email.toLowerCase(),
      name: formData.name,
      institution: formData.institution,
      about: formData.about,
      type: getUserTypeCode(formData.typeNumeric),
      tentative: "false",
      message: "",
    };

    const response = await addUser(postValues);

    if (response.status === 200) {
      setAlert({ type: "success", title: "Success!", message: "User Added!" });
      props.reloadImagesUsersCallBack(true);
      closeModal("addUser");
      resetForm();
    } else {
      setAlert({
        type: "error",
        title: "Error",
        message: response.data?.messages?.databaseError || "Failed to add user",
      });
    }
  };

  const handleSubmitUpdateUser = async (e) => {
    e.preventDefault();

    const postValues = {
      email: formData.email,
      name: formData.name,
      institution: formData.institution,
      about: formData.about,
      type: getUserTypeCode(formData.typeNumeric),
    };

    const response = await updateUser(formData.id, postValues);

    if (response.status === 200) {
      setAlert({
        type: "success",
        title: "Success!",
        message: "User Updated!",
      });
      props.reloadImagesUsersCallBack(true);
      closeModal("updateUser");
      resetForm();
    } else {
      setAlert({
        type: "error",
        title: "Error",
        message:
          response.data?.messages?.databaseError || "Failed to update user",
      });
    }
  };

  const handleDeleteUser = async (id) => {
    const response = await deleteUser(id);

    if (response.status === 200) {
      setAlert({
        type: "success",
        title: "Success!",
        message: "User Deleted!",
      });
      props.reloadImagesUsersCallBack(true);
    } else {
      setAlert({
        type: "error",
        title: "Error",
        message:
          response.data?.messages?.databaseError || "Failed to delete user",
      });
    }
  };

  const confirmDeleteUser = (id, name) => {
    setAlert({
      type: "warning",
      title: "Delete User?",
      message: `${name} will be permanently deleted`,
      onConfirm: () => handleDeleteUser(id),
      onCancel: () => setAlert(null),
      confirmText: "Yes, delete!",
      cancelText: "Cancel",
    });
  };

  // Tentative Management Functions
  const handleApproveUser = async (
    id,
    email,
    name,
    type,
    institution,
    about
  ) => {
    const postValues = {
      email,
      name,
      institution,
      about,
      type,
      tentative: "false",
    };

    const response = await approveTentative(id, postValues);

    if (response.status === 200) {
      setAlert({
        type: "success",
        title: "Success!",
        message: "User Approved!",
      });
      props.reloadImagesUsersCallBack(true);
      props.reloadTentativesCallBack && props.reloadTentativesCallBack(true);
    } else {
      setAlert({
        type: "error",
        title: "Error",
        message:
          response.data?.messages?.databaseError || "Failed to approve user",
      });
    }
  };

  const handleDenyUser = async (id) => {
    const response = await deleteTentative(id);

    if (response.status === 200) {
      setAlert({
        type: "success",
        title: "Success!",
        message: "Request Denied!",
      });
      props.reloadTentativesCallBack && props.reloadTentativesCallBack(true);
    } else {
      setAlert({
        type: "error",
        title: "Error",
        message:
          response.data?.messages?.databaseError || "Failed to deny request",
      });
    }
  };

  const confirmApproveUser = (id, email, name, type, institution, about) => {
    setAlert({
      type: "warning",
      title: "Approve Request?",
      message: `${name}'s request will be approved`,
      onConfirm: () =>
        handleApproveUser(id, email, name, type, institution, about),
      onCancel: () => setAlert(null),
      confirmText: "Yes, Approve!",
      cancelText: "Cancel",
    });
  };

  const confirmDenyUser = (id, name) => {
    setAlert({
      type: "warning",
      title: "Deny Request?",
      message: `${name}'s request will be denied`,
      onConfirm: () => handleDenyUser(id),
      onCancel: () => setAlert(null),
      confirmText: "Yes, Deny!",
      cancelText: "Cancel",
    });
  };

  // Registration Info Functions
  const [selectedRegistration, setSelectedRegistration] = React.useState(null);

  const openRegistrationModal = (registrationData) => {
    setSelectedRegistration(registrationData);
    openModal("registrationInfo");
  };

  const closeRegistrationModal = () => {
    setSelectedRegistration(null);
    closeModal("registrationInfo");
  };

  // Data helper functions
  const addRegistrationData = (registrationData) => {
    updateFormData("id", registrationData.id);
    updateFormData("agenda_time", registrationData.agenda_time);
    updateFormData("agenda_topic", registrationData.agenda_topic);
    updateFormData("confirmation_code", registrationData.confirmation_code);
    updateFormData("institution", registrationData.institution);
    updateFormData("email", registrationData.email);
    updateFormData("name", registrationData.name);
    updateFormData("need_time", registrationData.need_time);
    updateFormData("role", registrationData.role);
    updateFormData("registration_type", registrationData.registration_type);
  };

  // Data processing
  const voteData = processVoteData(props.attendance || []);
  const registrationData = processRegistrationData(
    props.registrations || [],
    formData.statNumeric,
    openRegistrationModal
  );
  const userData = processUserData(
    props.users || [],
    openModal,
    updateFormData,
    confirmDeleteUser
  );
  const attendanceData = processAttendanceData(props.attendance || []);
  const dataRequestsData = processDataRequestsData(
    props.dataRequests || [],
    updateFormData,
    openModal
  );
  const tentativeData = processTentativeData(
    props.tentatives || [],
    confirmApproveUser,
    confirmDenyUser
  );

  // Email lists
  const allUsers = props.users || [];
  const researchCoordinators = filterUsersByType(
    allUsers,
    "research_coordinator"
  );
  const usersWithoutResearchCoordinators = allUsers.filter(
    (user) => user.type !== "research_coordinator"
  );

  const userEmailsString = getEmailList(usersWithoutResearchCoordinators);
  const researchCoordinatorEmailsString = getEmailList(researchCoordinators);

  return (
    <div>
      <AlertManager alert={alert} setAlert={setAlert} />

      <TentativeTable
        tentatives={props.tentatives || []}
        tentativeData={tentativeData}
      />

      {/* Stats Selection - At the top */}
      <GridContainer>
        <GridItem xs={12} sm={12} md={12}>
          <Card>
            <CardHeader color="info" icon>
              <CardIcon color="info">
                <Assignment />
              </CardIcon>
              <h4 className={classes.cardIconTitle}>Admin Stats</h4>
            </CardHeader>
            <CardBody>
              <FormControl fullWidth>
                <InputLabel>Stats Type</InputLabel>
                <Select
                  value={formData.statNumeric}
                  onChange={(e) =>
                    updateFormData("statNumeric", e.target.value)
                  }
                >
                  {Object.values(STAT_TYPES).map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>

      {/* Stats Display - Right after selection */}
      <GridContainer>
        <StatsRenderer
          statNumeric={formData.statNumeric}
          userData={userData}
          registrationData={registrationData}
          attendanceData={attendanceData}
          userEmailsString={userEmailsString}
          researchCoordinatorEmailsString={researchCoordinatorEmailsString}
          dataRequestsData={dataRequestsData}
          voteData={voteData}
          onAddUser={() => {
            resetForm();
            openModal("addUser");
          }}
        />
      </GridContainer>

      {/* Modals */}
      <UserModal
        type="add"
        open={modals.addUser}
        onClose={() => closeModal("addUser")}
        formData={formData}
        formStates={formStates}
        updateFormData={updateFormData}
        updateFormState={updateFormState}
        onSubmit={handleSubmitAddUser}
        validateEmail={validateEmail}
        userTypes={USER_TYPES}
      />

      <UserModal
        type="update"
        open={modals.updateUser}
        onClose={() => closeModal("updateUser")}
        formData={formData}
        formStates={formStates}
        updateFormData={updateFormData}
        updateFormState={updateFormState}
        onSubmit={handleSubmitUpdateUser}
        validateEmail={validateEmail}
        userTypes={USER_TYPES}
      />

      {/* Registration Info Modal */}
      <RegistrationInfoModal
        open={modals.registrationInfo}
        onClose={closeRegistrationModal}
        registrationData={selectedRegistration}
      />

      {/* Data Requests Info Modal */}
      <DataRequestsInfoModal
        open={modals.dataRequestsInfo}
        onClose={() => closeModal("dataRequestsInfo")}
        formData={formData}
      />
    </div>
  );
}

Admin.propTypes = {
  users: PropTypes.array,
  tentatives: PropTypes.array,
  registrations: PropTypes.array,
  attendance: PropTypes.array,
  dataRequests: PropTypes.array,
  reloadImagesUsersCallBack: PropTypes.func,
  reloadTentativesCallBack: PropTypes.func,
};
